"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, MessageSquare, CircleCheck as CheckCircle2, PhoneCall, FlaskConical, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const benefits = [{
  title: "Method & Application Feasibility",
  description: "Evaluate instruments against your target analytes and compliance benchmarks."
}, {
  title: "Custom Qualification Roadmaps",
  description: "Tailored IQ/OQ protocols and compliance validation plans for your facility."
}, {
  title: "Dedicated Technical Specialists",
  description: "Direct access to senior application chemists across Ireland and the UK."
}];
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="testingapplicationcta" data-nav="light" className="relative overflow-hidden bg-background py-20 lg:py-28">
      <div className="page-container relative z-10">
        <div className="mx-auto max-w-5xl">
          <Card className="relative overflow-hidden border border-border bg-secondary/50 shadow-md backdrop-blur-xs">
            {/* Subtle background decoration */}
            <div className="pointer-events-none absolute -right-24 -top-24 size-96 rounded-full bg-primary/5 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/40 blur-3xl" />

            <CardContent className="p-8 md:p-12 lg:p-16">
              <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-12">
                
                {/* Left Column: Heading & Information */}
                <div className="lg:col-span-7">
                  <motion.div initial={shouldReduceMotion ? {} : {
                  opacity: 0,
                  y: 16
                }} whileInView={shouldReduceMotion ? {} : {
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  ease: "easeOut"
                }}>
                    <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-xs font-semibold">
                      <FlaskConical className="size-3.5 text-primary" />
                      Application Consultation
                    </Badge>
                    
                    <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                      Discuss Your Specific Testing Application
                    </h2>
                    
                    <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
                      Every laboratory workflow has distinct sensitivity, throughput, and regulatory constraints. Share your testing objectives with Antech Solutions, and our technical specialists will recommend the right instrumentation, sampling protocol, and qualification workflow.
                    </p>
                  </motion.div>

                  {/* Highlights list */}
                  <div className="mt-8 space-y-4">
                    {benefits.map((benefit, i) => <motion.div key={benefit.title} data-index={i} initial={shouldReduceMotion ? {} : {
                    opacity: 0,
                    x: -12
                  }} whileInView={shouldReduceMotion ? {} : {
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: 0.1 * i,
                    ease: "easeOut"
                  }} className="flex items-start gap-3">
                        <div className="mt-0.5 rounded-full bg-primary/10 p-1 text-primary">
                          <CheckCircle2 className="size-4" />
                        </div>
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            {benefit.title}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {benefit.description}
                          </p>
                        </div>
                      </motion.div>)}
                  </div>
                </div>

                {/* Right Column: Interactive Card & CTAs */}
                <div className="lg:col-span-5">
                  <motion.div initial={shouldReduceMotion ? {} : {
                  opacity: 0,
                  scale: 0.96
                }} whileInView={shouldReduceMotion ? {} : {
                  opacity: 1,
                  scale: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: 0.2,
                  ease: "easeOut"
                }} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6 shadow-sm sm:p-8">
                    <div className="flex items-center gap-3 border-b border-border pb-5">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <MessageSquare className="size-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-card-foreground">
                          Speak with a Specialist
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Direct review of your analytical requirements
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 py-2 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <Clock className="size-4 text-primary" />
                        <span>Response within 1 business day</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <PhoneCall className="size-4 text-primary" />
                        <span>Available for UK &amp; Ireland sites</span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 pt-3">
                      <Button asChild size="lg" className="w-full justify-center">
                        <Link to="/ContactPage" className="inline-flex items-center gap-2">
                          <span>Request Technical Review</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>

                      <Button asChild variant="outline" size="lg" className="w-full justify-center">
                        <Link to="/SolutionsPage">
                          <span>Explore Analytical Solutions</span>
                        </Link>
                      </Button>
                    </div>

                    <p className="text-center text-xs text-muted-foreground">
                      Confidential assessment tailored to GLP/GMP standards.
                    </p>
                  </motion.div>
                </div>

              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}